<!-- CodePreview.vue -->
<template>
  <div class="flex h-full min-h-0 bg-white border-l border-black/5">
    <div class="flex flex-1 min-h-0 w-full flex-col">
      <!-- Header (light chrome) -->
      <ActionHeader :name="name" :items="items">
        <template #actions>
          <!-- Line count -->
          <span
            class="text-[11px] tabular-nums text-slate-400 select-none hidden sm:inline"
          >
            {{ lineCount }} lines
          </span>
          <!-- Language badge -->
          <span
            v-if="resolvedLanguage"
            class="text-[11px] px-2 py-0.5 rounded font-mono bg-slate-100 text-slate-600 select-none hidden sm:inline"
            >{{ resolvedLanguage }}</span
          >
          <!-- Wrap toggle -->
          <button
            class="p-1.5 rounded transition-colors"
            :class="
              wrap
                ? 'bg-indigo-50 text-indigo-600'
                : 'text-slate-500 hover:bg-slate-100'
            "
            title="Toggle word wrap"
            @click="toggleWrap"
          >
            <Icon icon="mdi:wrap" class="text-base" />
          </button>
          <!-- Copy button -->
          <button
            class="p-1.5 rounded transition-colors"
            :class="
              copied
                ? 'bg-emerald-50 text-emerald-600'
                : 'text-slate-500 hover:bg-slate-100'
            "
            :title="copied ? 'Copied!' : 'Copy code'"
            @click="copy"
          >
            <Icon
              :icon="copied ? 'mdi:check' : 'mdi:content-copy'"
              class="text-base"
            />
          </button>
        </template>
      </ActionHeader>

      <!-- Body: dark editor -->
      <div class="relative flex-1 min-h-0">
        <div
          ref="scrollHost"
          class="code-scroll h-full w-full overflow-auto bg-white"
        >
          <div class="min-w-full text-[13px] leading-5 font-mono">
            <div class="flex items-start">
              <!-- Line numbers gutter -->
              <div
                class="shrink-0 select-none bg-gray-50 border-r border-gray-200 text-gray-400 text-right py-3"
                :class="wrap ? 'px-3' : 'px-4'"
              >
                <div
                  v-for="n in lineCount"
                  :key="n"
                  class="tabular-nums leading-5"
                >
                  {{ n }}
                </div>
              </div>

              <!-- Highlighted code -->
              <pre class="code-pre flex-1 min-w-0 m-0 py-3 px-4"><code
                  class="m-0 leading-5"
                  :class="[
                    langClass,
                    wrap ? 'whitespace-pre-wrap break-words' : 'whitespace-pre',
                  ]"
                  v-html="highlightedHtml"
                /></pre>
            </div>
          </div>
        </div>

        <p
          v-if="error"
          class="absolute bottom-0 left-0 right-0 px-4 py-3 text-sm text-red-500 bg-white/90 border-t border-red-200"
        >
          {{ error }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import hljs from "highlight.js"; // full build — all 190+ languages
import "highlight.js/styles/github.css";

hljs.configure({ ignoreUnescapedHTML: true });
import { Icon } from "@iconify/vue";

import apiClient from "@/services/api";
import { useApplicationStore } from "@/stores/ApplicationStore";
import { storeToRefs } from "pinia";
import { onDownload } from "@/helpers/download";
import { usePathsStore } from "@/stores/CartStore";
import { makeBaseItems } from "@/utils/makeBaseItems";
import ActionHeader from "@/components/ActionHeader.vue";

/* ----------------------- store & props ----------------------- */
const app = useApplicationStore();
const { current_selected } = storeToRefs(app);
const pathsStore = usePathsStore();

const props = defineProps({
  file: { type: String, default: "" }, // e.g., "/api/raw?path=foo.py"
  name: { type: String, default: "" },
  mime: { type: String, default: "" }, // e.g., "text/x-python"
  src: { type: String, default: "" }, // explicit URL (optional)
  code: { type: String, default: "" }, // preloaded code (optional)
  renderHtmlDefault: { type: Boolean, default: false },
});

/* ----------------------- derived ----------------------- */
const resolvedSrc = computed(() => props.src || props.file || "");
const ext = computed(() => {
  // An extensionless README is treated as markdown
  if ((props.name || "").toLowerCase() === "readme") return "md";
  const base = (props.file || props.name || "").toLowerCase();
  const i = base.lastIndexOf(".");
  return i >= 0 ? base.slice(i + 1) : "";
});

/* ----------------------- state ----------------------- */
const content = ref("");
const loaded = ref(false);
const error = ref("");
const wrap = ref(false);
const copied = ref(false);
const scrollHost = ref(null);

const baseItems = makeBaseItems({
  onDownload,
  apiClient: () => apiClient,
  selection: () => current_selected.value ?? null,
});

const items = computed(() => {
  const extra = pathsStore.hasName(props.name)
    ? []
    : [
        {
          type: "item",
          label: "add to the Download cart",
          icon: "mdi:file-document-add",
          action: () => addToMultipleDownload(),
        },
      ];
  return [...baseItems.value, ...extra];
});

function addToMultipleDownload() {
  const obj = { path: current_selected.value.path, name: props.name };
  pathsStore.add(obj);
}

// language hints
const langHintFromCT = ref(""); // from HTTP Content-Type
const renderAsHtml = ref(!!props.renderHtmlDefault);

/* ----------------------- language resolution ----------------------- */
const languageFromPropsMime = computed(() => {
  const m = (props.mime || "").toLowerCase();
  if (!m) return "";
  if (m.includes("python")) return "python";
  if (m.includes("javascript")) return "javascript";
  if (m.includes("typescript")) return "typescript";
  if (m.includes("markdown")) return "markdown";
  if (m.includes("bash") || m.includes("x-sh")) return "bash";
  if (m.includes("yaml") || m.includes("yml")) return "yaml";
  if (m.includes("json")) return "json";
  if (m.includes("sql")) return "sql";
  if (m.includes("ini")) return "ini";
  if (m.includes("html") || m.includes("xml") || m.includes("svg"))
    return "xml";
  if (m.includes("css")) return "css";
  return "";
});

const languageFromExt = computed(() => {
  switch ((ext.value || "").toLowerCase()) {
    // Web
    case "js":
    case "mjs":
    case "cjs":
      return "javascript";
    case "jsx":
      return "javascript";
    case "ts":
      return "typescript";
    case "tsx":
      return "typescript";
    case "vue":
      return "xml";
    case "html":
    case "htm":
      return "xml";
    case "xml":
    case "svg":
    case "xhtml":
      return "xml";
    case "css":
      return "css";
    case "scss":
    case "sass":
      return "scss";
    case "less":
      return "less";
    case "graphql":
    case "gql":
      return "graphql";
    // Scripting
    case "py":
    case "pyw":
    case "pyi":
      return "python";
    case "rb":
      return "ruby";
    case "php":
      return "php";
    case "lua":
      return "lua";
    case "pl":
    case "pm":
      return "perl";
    case "tcl":
      return "tcl";
    case "awk":
      return "awk";
    // Shell
    case "sh":
    case "bash":
    case "zsh":
    case "fish":
      return "bash";
    case "ps1":
    case "psm1":
    case "psd1":
      return "powershell";
    case "bat":
    case "cmd":
      return "dos";
    // Systems
    case "rs":
      return "rust";
    case "go":
      return "go";
    case "cpp":
    case "cxx":
    case "cc":
    case "c++":
      return "cpp";
    case "c":
      return "c";
    case "h":
    case "hh":
      return "c";
    case "hpp":
    case "hxx":
      return "cpp";
    case "cs":
      return "csharp";
    case "swift":
      return "swift";
    case "kt":
    case "kts":
      return "kotlin";
    case "java":
      return "java";
    case "scala":
      return "scala";
    case "groovy":
    case "gradle":
      return "groovy";
    case "dart":
      return "dart";
    case "zig":
      return "zig";
    case "nim":
      return "nim";
    case "d":
      return "d";
    case "v":
      return "verilog";
    case "vhd":
    case "vhdl":
      return "vhdl";
    // Functional / research
    case "hs":
    case "lhs":
      return "haskell";
    case "ex":
    case "exs":
      return "elixir";
    case "erl":
    case "hrl":
      return "erlang";
    case "fs":
    case "fsx":
    case "fsi":
      return "fsharp";
    case "ml":
    case "mli":
      return "ocaml";
    case "clj":
    case "cljs":
    case "cljc":
      return "clojure";
    case "scm":
    case "rkt":
      return "scheme";
    case "jl":
      return "julia";
    // Data science
    case "r":
    case "rmd":
      return "r";
    case "mat":
      return "matlab";
    case "f90":
    case "f95":
    case "f03":
    case "f08":
    case "for":
    case "f":
      return "fortran";
    // Data / config
    case "json":
    case "jsonc":
      return "json";
    case "yaml":
    case "yml":
      return "yaml";
    case "toml":
      return "ini";
    case "ini":
    case "cfg":
    case "conf":
      return "ini";
    case "env":
      return "properties";
    case "sql":
      return "sql";
    case "proto":
      return "protobuf";
    case "tf":
    case "tfvars":
      return "hcl";
    // Docs / markup
    case "md":
    case "mdx":
      return "markdown";
    case "tex":
    case "sty":
    case "cls":
      return "latex";
    case "rst":
      return "asciidoc";
    // Build / infra
    case "makefile":
    case "mk":
      return "makefile";
    case "dockerfile":
      return "dockerfile";
    case "nix":
      return "nix";
    case "cmake":
      return "cmake";
    case "vim":
    case "vimrc":
      return "vim";
    // Assembly
    case "asm":
    case "s":
      return "x86asm";
    case "wat":
      return "wasm";
    // Diff / misc
    case "diff":
    case "patch":
      return "diff";
    default:
      return "";
  }
});

const resolvedLanguage = computed(
  () =>
    langHintFromCT.value ||
    languageFromPropsMime.value ||
    languageFromExt.value ||
    "",
);

const langClass = computed(() =>
  resolvedLanguage.value ? `language-${resolvedLanguage.value}` : "",
);

const highlightedHtml = computed(() => {
  const raw = content.value || "";
  const escaped = raw
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  if (!raw) return "";
  const lang = resolvedLanguage.value;
  if (lang) {
    try {
      return hljs.highlight(raw, { language: lang }).value;
    } catch {
      /* fall through */
    }
  }
  try {
    return hljs.highlightAuto(raw).value;
  } catch {
    return escaped;
  }
});

/* ----------------------- HTML renderability ----------------------- */
const isHtmlRenderable = computed(() => {
  const m = (props.mime || "").toLowerCase();
  return m.includes("text/html") || ["html", "htm"].includes(ext.value);
});

/* ----------------------- line count (for gutter) ----------------------- */
const lineCount = computed(() => {
  if (!content.value) return 1;
  // Count \n; a trailing newline adds a blank last line which is fine
  return (content.value.match(/\n/g)?.length || 0) + 1;
});

/* ----------------------- load & highlight ----------------------- */
async function load(path) {
  error.value = "";
  loaded.value = false;

  try {
    if (props.code) {
      content.value = props.code;
      // If user provided raw code, assume we’re not sandbox-rendering HTML
      renderAsHtml.value = renderAsHtml.value && isHtmlRenderable.value;
    } else {
      if (!resolvedSrc.value && !path) {
        error.value = "No source provided.";
        loaded.value = true;
        return;
      }
      const p = path || resolvedSrc.value;
      const res = await apiClient.getFile(p);
      const ct = (res.headers?.["content-type"] || "").toString();
      langHintFromCT.value = guessLangFromMime(ct);

      // auto-enable iframe mode only when truly HTML documents
      if (!renderAsHtml.value && ct.toLowerCase().includes("text/html")) {
        renderAsHtml.value = true;
      }

      content.value =
        typeof res.data === "string" ? res.data : String(res.data ?? "");
    }
    content.value = (content.value || "").replace(/^\n+/, "");

    loaded.value = true;
  } catch (e) {
    error.value = `Failed to load file: ${e?.message || e}`;
    loaded.value = true;
  }
}

/* ----------------------- actions ----------------------- */
async function copy() {
  const text = content.value || "";
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.cssText = "position:fixed;top:0;left:0;opacity:0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    copied.value = true;
    setTimeout(() => {
      copied.value = false;
    }, 1500);
  } catch (e) {
    console.error("Copy failed:", e);
  }
}

function toggleWrap() {
  wrap.value = !wrap.value;
}

/* ----------------------- watchers ----------------------- */
watch(
  () => current_selected.value?.path,
  (p) => {
    if (p) load(p);
  },
  { immediate: true },
);

/* ----------------------- helpers ----------------------- */
function guessLangFromMime(ct) {
  if (!ct) return "";
  const raw = ct.toLowerCase().split(";")[0].trim();

  if (raw.includes("+")) {
    const suffix = raw.split("+").pop();
    if (suffix === "json") return "json";
    if (suffix === "xml") return "xml";
  }

  const map = {
    "text/plain": "plaintext",
    // web
    "text/javascript": "javascript",
    "application/javascript": "javascript",
    "application/x-javascript": "javascript",
    "text/ecmascript": "javascript",
    "application/ecmascript": "javascript",
    "application/typescript": "typescript",
    "text/typescript": "typescript",
    "text/html": "html",
    "text/css": "css",
    // data
    "application/json": "json",
    "text/json": "json",
    "application/xml": "xml",
    "text/xml": "xml",
    "application/x-yaml": "yaml",
    "text/yaml": "yaml",
    "application/yaml": "yaml",
    "application/toml": "toml",
    "text/markdown": "markdown",
    // shells & configs
    "application/x-sh": "bash",
    "application/x-bash": "bash",
    "text/x-sh": "bash",
    "text/x-shellscript": "bash",
    "application/x-powershell": "powershell",
    "text/x-powershell": "powershell",
    "application/x-dos-batch": "dos",
    "text/x-ini": "ini",
    // languages
    "text/x-python": "python",
    "application/x-python": "python",
    "text/x-go": "go",
    "text/x-java-source": "java",
    "text/x-csrc": "c",
    "text/x-c": "c",
    "text/x-c++src": "cpp",
    "text/x-cpp": "cpp",
    "text/x-csharp": "csharp",
    "text/x-rustsrc": "rust",
    "text/x-kotlin": "kotlin",
    "text/x-swift": "swift",
    "text/x-scala": "scala",
    "text/x-dart": "dart",
    "text/x-php": "php",
    "application/x-httpd-php": "php",
    "text/x-ruby": "ruby",
    "text/x-perl": "perl",
    "text/x-r": "r",
    "text/x-haskell": "haskell",
    "text/x-elixir": "elixir",
    "text/x-julia": "julia",
    "text/x-matlab": "matlab",
    "text/x-fortran": "fortran",
    "text/x-lua": "lua",
    "text/x-erlang": "erlang",
    "text/x-ocaml": "ocaml",
    "text/x-clojure": "clojure",
    "text/x-groovy": "groovy",
    "text/x-nim": "nim",
    "text/x-zig": "zig",
    "text/x-diff": "diff",
    "text/x-latex": "latex",
    "application/x-latex": "latex",
    "text/x-makefile": "makefile",
    "text/x-vim": "vim",
    "application/hcl": "hcl",
    "application/x-nix": "nix",
    // query
    "application/sql": "sql",
    "text/x-sql": "sql",
    "application/graphql": "graphql",
    "application/x-protobuf": "protobuf",
  };

  if (map[raw]) return map[raw];
  if (raw.endsWith("/json")) return "json";
  if (raw.endsWith("/xml")) return "xml";
  if (raw.startsWith("text/")) return "plaintext";
  return "";
}
</script>

<style scoped>
/* Code font stack */
.code-pre,
:deep(code) {
  font-family: "JetBrains Mono", "Fira Code", "Cascadia Code", "Menlo", "Monaco",
    "Consolas", ui-monospace, monospace;
  font-size: 13px;
}

/* Let the scrolling container supply the background, not the hljs code block */
:deep(code.hljs) {
  background: transparent !important;
  padding: 0 !important;
  display: block;
}
:deep(pre) {
  background: transparent !important;
  overflow-wrap: anywhere;
}

.code-scroll::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.code-scroll::-webkit-scrollbar-track {
  background: #f1f5f9;
}
.code-scroll::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
  border: 2px solid #f1f5f9;
}
.code-scroll::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
.code-scroll::-webkit-scrollbar-corner {
  background: #f1f5f9;
}

.tabular-nums {
  font-variant-numeric: tabular-nums;
  line-height: 1.25rem;
}
</style>
